import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Editor } from "@tinymce/tinymce-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listForms } from "../../../redux/actions/formsAction";
import {
  createSequence,
  updateSequence,
} from "../../../redux/actions/sequencesAction";
import { useSequenceDetail } from "./SequenceDetailContext";
import {
  DELAY_UNIT_OPTIONS,
  TRIGGER_TYPE_OPTIONS,
  emptyStep,
  sequenceDetailPath,
} from "./sequenceUtils";

const { Title, Text, Paragraph } = Typography;

const MAX_STEPS = 5;

function StepEditor({ step, index, total, onChange, onMove, onRemove }) {
  const update = (patch) => onChange({ ...step, ...patch });

  return (
    <Card
      size="small"
      title={`Step ${index + 1}`}
      extra={
        <Space>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === total - 1}
            onClick={() => onMove(index, 1)}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={total === 1}
            onClick={() => onRemove(index)}
          />
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space>
          <Text type="secondary">Send</Text>
          <InputNumber
            min={0}
            value={step.delay?.value}
            onChange={(value) =>
              update({ delay: { ...step.delay, value: value ?? 0 } })
            }
          />
          <Select
            style={{ width: 120 }}
            value={step.delay?.unit || "days"}
            options={DELAY_UNIT_OPTIONS}
            onChange={(unit) => update({ delay: { ...step.delay, unit } })}
          />
          <Text type="secondary">
            {index === 0 ? "after the contact enrols" : "after the previous step"}
          </Text>
        </Space>
        <div>
          <Text type="secondary">Subject</Text>
          <Input
            value={step.subject}
            onChange={(e) => update({ subject: e.target.value })}
            placeholder="Email subject"
          />
        </div>
        <div>
          <Text type="secondary">Body</Text>
          <Editor
            tinymceScriptSrc={"/tinymce/tinymce.min.js"}
            initialValue={step.html}
            onEditorChange={(html) => update({ html })}
            init={{
              height: 320,
              menubar: false,
              skin: "oxide",
              content_css: "default",
              plugins: ["link", "lists", "code"],
              toolbar:
                "undo redo | bold italic underline | bullist numlist | link | code",
              content_style: `
                body.mce-content-body {
                  background-color: #ffffff !important;
                  color: #111827 !important;
                }
                body.mce-content-body a { color: #2563eb !important; }
              `,
            }}
          />
        </div>
      </Space>
    </Card>
  );
}

export default function SequenceEditor() {
  const { id, isNew, sequenceRecord, reloadSequence } = useSequenceDetail();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formsList = useSelector((state) => state.forms.forms);

  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("tag_added");
  const [triggerFormId, setTriggerFormId] = useState(undefined);
  const [triggerTag, setTriggerTag] = useState("");
  const [exitTagAdded, setExitTagAdded] = useState("");
  const [steps, setSteps] = useState([emptyStep(0)]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(listForms({ pageSize: 100, pageNum: 1 })).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (isNew || !sequenceRecord) return;
    setName(sequenceRecord.name || "");
    setTriggerType(sequenceRecord.trigger?.type || "tag_added");
    setTriggerFormId(sequenceRecord.trigger?.formId || undefined);
    setTriggerTag(sequenceRecord.trigger?.tag || "");
    setExitTagAdded(sequenceRecord.exitOn?.tagAdded || "");
    setSteps(
      sequenceRecord.steps?.length
        ? sequenceRecord.steps.map((s, i) => ({ ...s, order: i }))
        : [emptyStep(0)],
    );
  }, [isNew, sequenceRecord]);

  const formOptions = useMemo(
    () => formsList.map((f) => ({ value: f._id, label: f.name })),
    [formsList],
  );

  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    setSteps((prev) => [...prev, emptyStep(prev.length)]);
  };

  const moveStep = (index, delta) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const buildTrigger = () => {
    if (triggerType === "form_submit" || triggerType === "doi_confirmed") {
      return { type: triggerType, formId: triggerFormId || null };
    }
    if (triggerType === "tag_added") {
      return { type: triggerType, tag: triggerTag.trim() };
    }
    return { type: triggerType };
  };

  const handleSave = async () => {
    if (!name.trim()) {
      message.error("Name is required.");
      return;
    }
    if (
      (triggerType === "form_submit" || triggerType === "doi_confirmed") &&
      !triggerFormId
    ) {
      message.error("Choose a form for this trigger.");
      return;
    }
    if (triggerType === "tag_added" && !triggerTag.trim()) {
      message.error("Enter a tag for this trigger.");
      return;
    }
    if (!steps.length) {
      message.error("Add at least one step.");
      return;
    }
    for (const [i, step] of steps.entries()) {
      if (!step.subject?.trim() || !step.html?.trim()) {
        message.error(`Step ${i + 1} needs a subject and body.`);
        return;
      }
    }

    const payload = {
      name: name.trim(),
      trigger: buildTrigger(),
      steps: steps.map((s, i) => ({
        order: i,
        templateRef: null,
        subject: s.subject.trim(),
        html: s.html,
        delay: { value: Number(s.delay?.value) || 0, unit: s.delay?.unit || "days" },
        sendWindow: null,
      })),
      exitOn: { tagAdded: exitTagAdded.trim() || null },
    };

    setSaving(true);
    try {
      if (isNew) {
        const created = await dispatch(createSequence(payload));
        message.success("Sequence created");
        navigate(sequenceDetailPath(created._id), { replace: true });
      } else {
        await dispatch(updateSequence(payload, id));
        message.success("Sequence saved");
        reloadSequence();
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || "Failed to save sequence",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card size="small" title="Details">
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text type="secondary">Name</Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Welcome series"
            />
          </div>
          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Trigger</Text>
              <Select
                style={{ width: "100%" }}
                value={triggerType}
                options={TRIGGER_TYPE_OPTIONS}
                onChange={setTriggerType}
              />
            </Col>
            <Col span={12}>
              {(triggerType === "form_submit" ||
                triggerType === "doi_confirmed") && (
                <>
                  <Text type="secondary">Form</Text>
                  <Select
                    style={{ width: "100%" }}
                    value={triggerFormId}
                    options={formOptions}
                    placeholder="Choose a form"
                    onChange={setTriggerFormId}
                  />
                </>
              )}
              {triggerType === "tag_added" && (
                <>
                  <Text type="secondary">Tag</Text>
                  <Input
                    value={triggerTag}
                    onChange={(e) => setTriggerTag(e.target.value)}
                    placeholder="e.g. webinar-signup"
                  />
                </>
              )}
            </Col>
          </Row>
          {triggerType === "segment_entered" && (
            <Alert
              type="info"
              showIcon
              message="Segments aren't available yet"
              description="This trigger will work once segment building ships; the sequence will stay in draft until then."
            />
          )}
          <div>
            <Text type="secondary">
              Also exit contacts when this tag is added (optional)
            </Text>
            <Input
              value={exitTagAdded}
              onChange={(e) => setExitTagAdded(e.target.value)}
              placeholder="e.g. customer"
            />
          </div>
          <Checkbox checked disabled>
            Always exit a contact who unsubscribes (fixed)
          </Checkbox>
        </Space>
      </Card>

      <div>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={5} style={{ margin: 0 }}>
            Steps
          </Title>
          <Button
            icon={<PlusOutlined />}
            disabled={steps.length >= MAX_STEPS}
            onClick={addStep}
          >
            Add step
          </Button>
        </Space>
        <Paragraph type="secondary" style={{ marginTop: 4 }}>
          Up to {MAX_STEPS} steps, sent in order with the delay you set on each.
        </Paragraph>
      </div>

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {steps.map((step, index) => (
          <StepEditor
            key={index}
            step={step}
            index={index}
            total={steps.length}
            onChange={(next) =>
              setSteps((prev) => prev.map((s, i) => (i === index ? next : s)))
            }
            onMove={moveStep}
            onRemove={(removeIndex) =>
              setSteps((prev) =>
                prev
                  .filter((_, i) => i !== removeIndex)
                  .map((s, i) => ({ ...s, order: i })),
              )
            }
          />
        ))}
      </Space>

      <Button type="primary" loading={saving} onClick={handleSave}>
        {isNew ? "Create sequence" : "Save sequence"}
      </Button>
    </Space>
  );
}
